<script setup>
/**
 * SpreadReading — ONE spread-agnostic reading layout (design v4 "Adaptive Spotlight").
 *
 * Replaces the bespoke OneCardReading / FourCardReading layouts with a single
 * component that reshapes to the spread:
 *   • LINEAR spreads (1 / 3 / 4 / 5 …)  → a filmstrip navigator + a big spotlight.
 *   • POSITIONAL spreads (Celtic Cross) → a compact tiny-card spatial MAP (true
 *     cross+staff geometry, kept as a map at every screen size) + the spotlight.
 * Tapping a card in the navigator fills the spotlight with that position's card +
 * meaning. The woven synthesis + illustration sit in the footer.
 *
 * Purely presentational + host-agnostic: the live reading and the Studio preview
 * both render through this, so they can't drift. Paywall and illustration are
 * SLOTS — each host wires its own gating / image logic.
 *
 * card shape: { position, name, image, reversed, meaning }
 *   (hosts normalise image_path|image_url → image before passing in.)
 */
import { ref, computed, watch, nextTick } from 'vue'
import { linkCardNames } from '@/utils/card-links'
import { summaryFitStyle } from '@/utils/summary-fit'
import CaptionBeats from '@/components/ui/share/CaptionBeats.vue'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  question: { type: String, default: '' },
  cards: { type: Array, default: () => [] },
  // The closing position — the last card of the spread, revealed AFTER the
  // weave ({ position, name, image, reversed, meaning }). It used to be dropped
  // from this pane and left to the chat, so a two-card Fork showed one card,
  // no strip, and no way to reach "The Move". It is a sealed last step now: in
  // the strip, behind the summary, whose Next becomes "Reveal The Move".
  closing: { type: Object, default: null },
  synthesis: { type: String, default: '' },
  // The reading's summary headline (e.g. "Fair Emerges"), shown above the weave.
  title: { type: String, default: '' },
  // Illustration URL; when absent the #illustration slot renders instead.
  image: { type: String, default: '' },
  // 'line' | 'cross'. Empty → inferred from card count (10 ⇒ cross).
  archetype: { type: String, default: '' },
  // Paywall: cards from this index on are gated while `locked` is true.
  gateFrom: { type: Number, default: -1 },
  locked: { type: Boolean, default: false },
  // How gating looks: true → hide the gated card faces (4-card hides
  // future/advice); false → keep the face visible, lock only the meaning
  // (1-card shows the card, paywalls the interpretation).
  gateHidesCard: { type: Boolean, default: true },
  // Studio viewport simulation: '' (real responsive) | 'desktop'|'tablet'|'mobile'.
  viewport: { type: String, default: '' },
  // Show the footer illustration block (hosts hide it until a reading is paid).
  showIllustration: { type: Boolean, default: true },
  // Live reading: gate the footer summary (weave + illustration) behind a final
  // "Summary" step. The seeker pages through the cards; on the last card the
  // Next button becomes "Summary", and clicking it reveals + scrolls to the
  // footer (Prev stays so they can page back). When false (Studio preview /
  // 1-card) the footer is always shown.
  revealSummary: { type: Boolean, default: false },
  // The illustration's title. It exists on every reading but was only ever
  // shown in the expanded lightbox, so the picture sat captionless on the page.
  caption: { type: String, default: '' },
  // When true, the reveal is an AUTH gate (a logged-out shared-reading viewer):
  // the scrim/Summary button does NOT open the summary — it asks the host to
  // log the viewer in (emit 'reveal-auth'). Once logged in the host drops
  // revealSummary, so the summary shows without a second click.
  authGate: { type: Boolean, default: false },
  // When true, the spotlight card opens a detail view (emit 'card-detail' with the
  // card index) and the illustration is click-to-enlarge (emit 'zoom' with the url).
  // Off by default so other hosts are unaffected.
  zoomable: { type: Boolean, default: false },
  // Cards pulled after the reading (Talk it through, 2026-10-21): their steps
  // follow the spread's in the strip after a ✦ ("Pulled", "Pulled 1", …).
  // A tap emits 'extra-step' with the index; the host shows that card.
  extraSteps: { type: Array, default: () => [] },
})
const emit = defineEmits(['select', 'reveal-auth', 'zoom', 'card-detail', 'summary-visible', 'extra-step'])

// The caption arrives with its picture (picture-caption DESIGN.md, 2026-10-30):
// never under an empty frame. A picture through the #illustration slot calls
// the slot's `loaded` from its own @load.
const illLoaded = ref(false)
function illustrationLoaded() { illLoaded.value = true }
watch(() => props.image, () => { illLoaded.value = false })

const sel = ref(0)
const footEl = ref(null)
const stripEl = ref(null)
// Keep the active position tab visible when the seeker steps through cards via
// the carousel arrows (the strip scrolls horizontally when it can't fit all tabs).
function scrollSelIntoView() {
  nextTick(() => {
    const strip = stripEl.value
    const active = strip && strip.querySelector('.sr-step.sel')
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }
  })
}
// Carousel arrows: cycle the spotlight to the prev/next card (clamped).
function stripStep(dir) {
  const n = allCards.value.length
  const next = Math.max(0, Math.min(n - 1, spotIndex.value + dir))
  pickAndScroll(next)
}
function pickAndScroll(i) {
  // The closing card is reached through the summary; sealed, it does not open.
  if (i === props.cards.length && props.closing) {
    if (closingSealed.value) return
    if (hasClosingStep.value) { revealClosing(); return }
  }
  pick(i); scrollSelIntoView()
}
function revealClosing() {
  sel.value = closingIndex.value
  emit('select', closingIndex.value)
  nextTick(() => { scrollSelIntoView(); slowScrollTo(spotEl.value) })
}
const spotEl = ref(null)
// Once the seeker reaches the trailing "Summary" step the footer (weave +
// illustration) is revealed, and stays revealed if they page back.
const summaryOpen = ref(false)
const hasFooterContent = computed(() => !!props.synthesis || !!props.title || props.showIllustration)
// Reveal mode: a trailing "Summary" step gates the footer (multi-card live
// readings). Otherwise the footer always shows (Studio preview / 1-card).
// Every position on the table, closing included — the strip and the body
// layout count these. Without a reveal step (Studio preview, history) the
// closing card is simply the last card.
const allCards = computed(() => props.closing ? [...props.cards, { ...props.closing, _closing: true }] : props.cards)
// Up to five position tabs wrap onto a second line rather than run off the
// strip mid-word (e2e round 2, phone S01); a longer spread keeps the carousel.
const stripWraps = computed(() => allCards.value.length + (props.extraSteps || []).length <= 5)
const hasSummaryStep = computed(() => props.revealSummary && allCards.value.length > 1 && hasFooterContent.value)
const hasClosingStep = computed(() => !!props.closing && hasSummaryStep.value)
// Steps: the open cards, then the summary, then the closing card.
const summaryIndex = computed(() => props.cards.length)
const closingIndex = computed(() => props.cards.length + 1)
const stepCount = computed(() => hasClosingStep.value
  ? props.cards.length + 1 + 1                              // cards, summary, closing
  : allCards.value.length + (hasSummaryStep.value ? 1 : 0)) // closing folded in as a card
const onClosing = computed(() => hasClosingStep.value && sel.value === closingIndex.value)
// On the summary step of a spread with a closing card, the reveal button moves
// to the END of the weave: the summary scroll lands there, while the spotlight
// nav has scrolled out of view — a button the seeker cannot see is no button.
const onSummaryBeforeClosing = computed(() => hasClosingStep.value && sel.value === summaryIndex.value)
// The footer stays MOUNTED from the start so the illustration preloads (this is
// what the old opaque scrim was really buying), but until the seeker steps to
// the summary it is collapsed to zero height and its words are not rendered at
// all — so nothing of the conclusion, title included, reaches the page early.
// One affordance opens it: "Summary" on the last card.
// A LOCKED live reading gates the whole summary (weave + illustration) behind the
// paywall — for EVERY spread size, including 1/2-card where no back-half card
// exists to gate against. (Studio preview + SeekerModal pass locked=false, so
// they're unaffected.) This is what makes "the cards are explained, then you get
// a [gated] summary" true for 1- and 2-card draws.
const summaryLocked = computed(() => props.locked)
const showFooter = computed(() => hasFooterContent.value || summaryLocked.value)
// Sealed ONLY when there is a summary step to unseal it. A 1-card draw has no
// trailing step (hasSummaryStep requires >1 card), so without this it would
// seal with nothing able to open it — the removed scrim button used to be its
// only escape hatch.
const summarySealed = computed(() => hasSummaryStep.value && !summaryOpen.value)
watch(() => props.cards, () => { if (sel.value > stepCount.value - 1) sel.value = 0 })

// Tell the host when the weave is actually on screen. Rate / Share hang off
// this: they were appearing the moment the reading was delivered, i.e. while
// the seeker was still on card 1 with the summary sealed. A reading with no
// seal (1-card, or opened from history) reports true straight away.
watch(summarySealed, (sealed) => emit('summary-visible', !sealed), { immediate: true })

const isCross = computed(() =>
  (props.archetype || (props.cards.length === 10 ? 'cross' : 'line')) === 'cross')
// On the summary step the spotlight keeps the LAST card in view (we only reveal
// the footer below), so clamp the spotlight index to the card range.
const spotIndex = computed(() => {
  if (onClosing.value) return props.cards.length            // the closing card's slot in allCards
  if (!hasClosingStep.value && props.closing) return Math.min(sel.value, allCards.value.length - 1)
  return Math.min(sel.value, props.cards.length - 1)
})
const current = computed(() => allCards.value[spotIndex.value] || null)
const closingSealed = computed(() => hasClosingStep.value && !summaryOpen.value)
const isGated = (i) => props.locked && props.gateFrom >= 0 && i >= props.gateFrom
const currentGated = computed(() => isGated(spotIndex.value))
// Whether the card's ART is hidden (vs. just its meaning locked).
const faceHidden = (i) => isGated(i) && props.gateHidesCard
const currentFaceHidden = computed(() => faceHidden(spotIndex.value))

const GLYPH = {
  'The Star': '✶', 'The Tower': '⚡', 'Two of Cups': '⚭', 'The Sun': '☉',
  'Eight of Pentacles': '⛏', 'Justice': '⚖', 'The Moon': '☾', 'Wheel of Fortune': '☸',
  'The Lovers': '⚭', 'Five of Swords': '⚔', 'Four of Pentacles': '⬢', 'The Hermit': '☀',
  'Page of Wands': '❧', 'The Emperor': '♔', 'Three of Cups': '⚘', 'The World': '❂',
}
const glyph = (n) => GLYPH[n] || '✦'

function pick(i) { sel.value = i; emit('select', i) }
// The table row's tiles open the pane on a card (2026-10-21).
defineExpose({ pickAndScroll })
// The words are links: a card named in the prose opens its detail, the same
// as tapping its face (utils/card-links). Indexes are slots in allCards, so
// the closing card resolves too. Plain text for hosts with no detail to open.
const prose = (text) => (props.zoomable ? linkCardNames(text, allCards.value) : [{ text }])
// The summary's type follows its length and its box (utils/summary-fit).
const synFit = computed(() => summaryFitStyle(props.synthesis))
function openDetail(i) { if (props.zoomable) emit('card-detail', i) }
// Linear paging (no wrap) so the last card's Next can become "Summary".
function step(d) { const n = stepCount.value; sel.value = Math.max(0, Math.min(n - 1, sel.value + d)) }
function openSummary() {
  // Logged-out shared viewer: the reveal is the login gate, not the summary.
  if (props.authGate) { emit('reveal-auth'); return }
  sel.value = props.cards.length            // the trailing summary step
  summaryOpen.value = true
  nextTick(() => slowScrollTo(footEl.value))
}

// Reveal lands on what it revealed (gift bug walk 2026-10-25 #13): the first
// card it opened, or, when only the summary was sealed, the summary itself
// (once its words are there). Tapping Reveal used to leave the spotlight
// where it was, so it looked as if nothing happened.
let landOnReveal = false
function landReveal() {
  if (!landOnReveal || props.authGate) return
  const g = props.gateFrom
  if (g >= 0 && g < props.cards.length) { landOnReveal = false; if (g !== sel.value) pickAndScroll(g); return }
  if (!hasSummaryStep.value) return
  landOnReveal = false
  openSummary()
}
watch(() => props.locked, (now, was) => { if (was && !now) { landOnReveal = true; landReveal() } })
watch(hasSummaryStep, () => landReveal())

/**
 * Drift down to the summary rather than snapping to it.
 *
 * The summary is the one part of the reading kept below the fold, so arriving
 * at it should read as a turn of the page. The browser's own 'smooth' is a few
 * hundred milliseconds and lands before the eye follows; this eases over ~1.6s
 * so the cards visibly give way to the weave.
 *
 * Falls back to an instant jump when the seeker has asked for reduced motion,
 * or when there is no scroller to animate.
 */
function slowScrollTo(el, duration = 1600) {
  if (!el) return
  const reduced = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduced || typeof requestAnimationFrame !== 'function') {
    if (el.scrollIntoView) el.scrollIntoView({ block: 'start' })
    return
  }

  // The reading sits in a pane that scrolls, not the window — walk up to
  // whichever ancestor actually has the overflow.
  let sc = el.parentElement
  while (sc && sc !== document.body) {
    const oy = getComputedStyle(sc).overflowY
    if ((oy === 'auto' || oy === 'scroll') && sc.scrollHeight > sc.clientHeight) break
    sc = sc.parentElement
  }
  if (!sc || sc === document.body) {
    if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  const start = sc.scrollTop
  const target = Math.min(
    start + el.getBoundingClientRect().top - sc.getBoundingClientRect().top,
    sc.scrollHeight - sc.clientHeight)
  const dist = target - start
  if (Math.abs(dist) < 2) return

  // easeInOutCubic — unhurried at both ends, so it neither jerks off the mark
  // nor overshoots into a stop.
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
  let t0 = null
  const tick = (now) => {
    if (t0 === null) t0 = now
    const p = Math.min(1, (now - t0) / duration)
    sc.scrollTop = start + dist * ease(p)
    if (p < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}
// Next: advance a card, or reveal the summary when stepping off the last card.
function next() {
  if (hasSummaryStep.value && sel.value === props.cards.length - 1) openSummary()
  else if (hasClosingStep.value && sel.value === summaryIndex.value) revealClosing()
  else step(1)
}

// Celtic geometry: index → grid slot. 0=heart(center) 1=crossing 2=below 3=left
// 4=above 5=right; 6..9 = staff (rendered 10→7 top-to-bottom).
const crossSlots = [
  { i: 4, cls: 'p5' }, { i: 3, cls: 'p4' }, { i: 5, cls: 'p6' }, { i: 2, cls: 'p3' },
]
const staffOrder = [9, 8, 7, 6]
</script>

<template>
  <div class="spread-reading" :class="viewport ? `sr-vp-${viewport}` : ''">
    <header v-if="question" class="sr-hero">
      <h1>{{ question }}</h1>
      <div class="sr-rule"></div>
    </header>

    <div class="sr-body" :class="isCross ? 'sr-side' : (allCards.length > 1 ? 'sr-linebody' : 'sr-stack')">
      <!-- ── navigator: tiny-card MAP (cross) or FILMSTRIP (line) ── -->
      <nav v-if="isCross" class="sr-mapbox" aria-label="Spread map">
        <div class="sr-map-title">The Spread</div>
        <div class="sr-tmap">
          <div class="sr-tcross">
            <div class="sr-pc">
              <div class="sr-pc-a">
                <button class="sr-tc" :class="{ sel: sel === 0, gated: faceHidden(0) }"
                  :title="cards[0] ? cards[0].position : ''" @click="pick(0)">
                  <span class="sr-num">1</span><span class="sr-tg">{{ faceHidden(0) ? '🔒' : glyph(cards[0] && cards[0].name) }}</span>
                </button>
              </div>
              <div class="sr-pc-b">
                <button class="sr-tc" :class="{ sel: sel === 1, gated: faceHidden(1) }"
                  :title="cards[1] ? cards[1].position : ''" @click="pick(1)">
                  <span class="sr-num">2</span><span class="sr-tg">{{ faceHidden(1) ? '🔒' : glyph(cards[1] && cards[1].name) }}</span>
                </button>
              </div>
            </div>
            <button v-for="s in crossSlots" :key="s.cls" class="sr-tc" :class="[s.cls, { sel: sel === s.i, gated: faceHidden(s.i) }]"
              :title="cards[s.i] ? cards[s.i].position : ''" @click="pick(s.i)">
              <span class="sr-num">{{ s.i + 1 }}</span><span class="sr-tg">{{ faceHidden(s.i) ? '🔒' : glyph(cards[s.i] && cards[s.i].name) }}</span>
            </button>
          </div>
          <div class="sr-tstaff">
            <button v-for="i in staffOrder" :key="i" class="sr-tc" :class="{ sel: sel === i, gated: faceHidden(i) }"
              :title="cards[i] ? cards[i].position : ''" @click="pick(i)">
              <span class="sr-num">{{ i + 1 }}</span><span class="sr-tg">{{ faceHidden(i) ? '🔒' : glyph(cards[i] && cards[i].name) }}</span>
            </button>
          </div>
        </div>
      </nav>

      <!-- ── spotlight ── -->
      <section class="sr-spot" ref="spotEl" :class="{ 'sr-closing': onClosing }">
        <div class="sr-scard">
          <div class="sr-frame sr-big" :class="{ rev: current && current.reversed, gated: currentFaceHidden }">
            <img v-if="current && current.image && !currentFaceHidden" :src="current.image" :alt="current.name"
              class="sr-img" :class="{ 'sr-zoomable': zoomable }" @click="zoomable && $emit('card-detail', spotIndex)" />
            <template v-else>
              <span class="sr-glyph">{{ currentFaceHidden ? '🔒' : glyph(current && current.name) }}</span>
              <span v-if="current && !currentFaceHidden" class="sr-fname">{{ current.name }}</span>
            </template>
          </div>
          <!-- card name sits below the card (when revealed); the "· Reversed"
               suffix carries the orientation, so no separate reversed indicator. -->
          <div v-if="current && !currentFaceHidden" class="sr-mcard sr-mcard-under" :class="{ 'sr-cardlink': zoomable }"
            :role="zoomable ? 'button' : null" :tabindex="zoomable ? 0 : null"
            @click="openDetail(spotIndex)" @keydown.enter="openDetail(spotIndex)">{{ current.name }}<span v-if="current.reversed"> · Reversed</span></div>
        </div>

        <div class="sr-stext">
          <!-- position word-strip — sits in the text column so it aligns with
               the paragraph + the (centered) Prev/Next below. Same navigation. -->
          <div v-if="!isCross && (allCards.length > 1 || extraSteps.length)" class="sr-stripwrap">
            <button type="button" class="sr-striparrow" :disabled="spotIndex === 0"
              @click="stripStep(-1)" aria-label="Previous card">‹</button>
            <nav class="sr-strip" :class="{ 'sr-strip-wrap': stripWraps }" ref="stripEl" aria-label="Positions">
              <button v-for="(c, i) in allCards" :key="i" type="button" class="sr-step"
                :class="{ sel: spotIndex === i, sealed: c._closing && closingSealed }"
                :aria-disabled="c._closing && closingSealed ? 'true' : null"
                :title="c._closing && closingSealed ? 'Revealed after the summary' : ''"
                @click="pickAndScroll(i)">{{ c.position }}</button>
              <button v-for="(label, k) in extraSteps" :key="'x' + k" type="button" class="sr-step sr-step-pulled"
                data-testid="strip-pulled" @click="$emit('extra-step', k)">✦ {{ label }}</button>
            </nav>
            <button type="button" class="sr-striparrow" :disabled="spotIndex === allCards.length - 1 || (closingSealed && spotIndex === cards.length - 1)"
              @click="stripStep(1)" aria-label="Next card">›</button>
          </div>
          <!-- paragraph + Prev/Next: vertically centered against the card,
               while the word-strip above stays anchored to the top. -->
          <div class="sr-mid">
            <template v-if="currentGated">
              <slot name="paywall" :position="current ? current.position : ''" :is-summary="false">
                <p class="sr-locked">Unlock to reveal this card.</p>
              </slot>
            </template>
            <template v-else-if="current && current._closing && !current.meaning">
              <!-- The closing card's words arrive after the weave; the face is here. -->
              <div class="sr-meaning sr-waiting">{{ current.position }} is being written…</div>
            </template>
            <!-- A card with no words yet draws no box: an empty rounded box sat
                 between the tabs and Next (loop walk 2026-10-26 #6). -->
            <template v-else-if="current && String(current.meaning || '').trim()">
              <div class="sr-meaning" data-testid="sr-meaning"><template v-for="(seg, k) in prose(current.meaning)" :key="k"><button
                v-if="seg.card !== undefined" type="button" class="sr-cardlink" @click="openDetail(seg.card)">{{ seg.text }}</button><template v-else>{{ seg.text }}</template></template></div>
            </template>
            <!-- Prev / Next (or the trailing "Summary" reveal) below the meaning. -->
            <div v-if="stepCount > 1 && !onSummaryBeforeClosing" class="sr-nav sr-nav-under">
              <button v-if="sel > 0" type="button" @click="step(-1)">
                {{ hasSummaryStep && sel === summaryIndex ? '‹ Back to the cards' : (onClosing ? '‹ Summary' : '‹ Prev') }}
              </button>
              <button v-if="sel < stepCount - 1" type="button" @click="next()">
                {{ hasSummaryStep && sel === cards.length - 1 ? 'Summary'
                  : (hasClosingStep && sel === summaryIndex ? `Reveal ${closing.position} ›` : 'Next ›') }}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- ── footer: the weave (synthesis + title) + a large illustration, below the cards ── -->
    <footer
      v-if="showFooter" ref="footEl" class="sr-foot"
      :class="{ 'sr-foot-sealed': summarySealed }"
      :aria-hidden="summarySealed ? 'true' : null"
    >
      <!-- LOCKED: the summary is gated for EVERY spread (this is the only gate on
           a 1/2-card draw). Render the host paywall in the summary's place rather
           than leaving a blank void; no summary content is passed while locked. -->
      <template v-if="summaryLocked">
        <div class="sr-foot-body sr-foot-gated">
          <slot name="paywall" :position="''" :is-summary="true">
            <p class="sr-locked">Unlock to reveal your reading.</p>
          </slot>
        </div>
      </template>
      <template v-else>
      <!-- The words are withheld entirely while sealed — the title is the
           conclusion's headline, so showing it early gave the ending away.
           The illustration stays mounted so it is painted and ready. -->
      <h2 v-if="title && !summarySealed" class="sr-foot-title">{{ title }}</h2>
      <div class="sr-foot-body" :class="{ 'sr-foot-solo': !synthesis }">
        <div v-if="synthesis && !summarySealed" class="sr-syn" :style="synFit">
          <p><template v-for="(seg, k) in prose(synthesis)" :key="k"><button
            v-if="seg.card !== undefined" type="button" class="sr-cardlink" @click="openDetail(seg.card)">{{ seg.text }}</button><template v-else>{{ seg.text }}</template></template></p>
          <!-- The weave is not the ending when a closing card waits: the reveal
               sits right under the words, where the summary scroll lands
               (below the illustration it fell under the pane's fold). -->
          <div v-if="onSummaryBeforeClosing" class="sr-nav sr-foot-nav">
            <button type="button" @click="step(-1)">‹ Back to the cards</button>
            <button type="button" class="sr-reveal" @click="revealClosing()">Reveal {{ closing.position }} ›</button>
          </div>
        </div>
        <figure v-if="showIllustration" class="sr-ill">
          <div class="sr-slot">
            <img v-if="image" :src="image" :alt="caption || 'Reading illustration'"
              class="sr-ill-img" :class="{ 'sr-zoomable': zoomable }" @load="illustrationLoaded" @click="zoomable && $emit('zoom', image)" />
            <slot v-else name="illustration" :loaded="illustrationLoaded"></slot>
          </div>
          <figcaption v-if="caption && illLoaded && !summarySealed" class="sr-ill-cap"><CaptionBeats :text="caption" /></figcaption>
        </figure>
      </div>
      <!-- No synthesis to hang the reveal under: it stands here instead. -->
      <div v-if="!summarySealed && onSummaryBeforeClosing && !synthesis" class="sr-nav sr-foot-nav">
        <button type="button" @click="step(-1)">‹ Back to the cards</button>
        <button type="button" class="sr-reveal" @click="revealClosing()">Reveal {{ closing.position }} ›</button>
      </div>
      <!-- The reading has an ending; give it one — once the closing card is out. -->
      <div v-else-if="!summarySealed && !onSummaryBeforeClosing" class="sr-end" aria-hidden="true">✦</div>
      </template>
    </footer>
  </div>
</template>

<style scoped>
.spread-reading{
  --paper:#fbf7f0;--paper-2:#f3ead9;--gilt:#b8884a;--gilt-soft:rgba(184,136,74,.45);
  --lilac:#d4bef0;--lilac-soft:rgba(212,190,240,.30);--veil:#6b3fa0;--ink:#2a1845;--ink-soft:#5a4a72;
  --serif:'Cinzel',Georgia,serif;--cw:118px;--ch:188px;--tw:46px;--th:70px;
  container-type:inline-size; color:var(--ink); font-family:'Cormorant Garamond',Georgia,serif;
  /* Self-contained parchment surface so the reading reads correctly on ANY host
     (the live dark page, the Studio modal, the mockups). */
  background: radial-gradient(700px 360px at 50% 0%, #fffdf8 0%, var(--paper) 55%, var(--paper-2) 100%);
  border: 1px solid var(--gilt-soft);
  border-radius: 16px;
  padding: clamp(20px, 3vw, 36px);
  box-shadow: 0 18px 50px rgba(0,0,0,.35), inset 0 0 0 1px rgba(255,255,255,.45);
  box-sizing: border-box;
}
.sr-vp-desktop{max-width:1060px}.sr-vp-tablet{max-width:760px}.sr-vp-mobile{max-width:392px}
.sr-vp-desktop,.sr-vp-tablet,.sr-vp-mobile{margin:0 auto}

.sr-hero{text-align:center;margin-bottom:6px}
.sr-hero h1{font-family:var(--serif);color:var(--ink);font-weight:600;font-size:clamp(20px,3vw,28px);margin:0;line-height:1.25}
.sr-rule{width:110px;height:2px;margin:12px auto 0;background:linear-gradient(90deg,transparent,var(--gilt),transparent)}

.sr-chip{display:inline-flex;align-items:center;gap:6px;font-family:var(--serif);font-size:11px;font-weight:600;
  letter-spacing:.10em;text-transform:uppercase;color:var(--veil);background:var(--lilac-soft);
  border:1px solid var(--lilac);border-radius:999px;padding:4px 12px;white-space:nowrap}
.sr-dot{width:6px;height:6px;border-radius:50%;background:var(--gilt)}

/* card frame */
.sr-frame{width:var(--cw);height:var(--ch);border-radius:11px;position:relative;overflow:hidden;
  background:radial-gradient(120% 80% at 50% 0%,rgba(255,255,255,.10),transparent 60%),linear-gradient(160deg,#3a2566,#1c1030 60%,#2a1845);
  border:2px solid var(--gilt);box-shadow:0 8px 22px rgba(0,0,0,.4),inset 0 0 0 4px rgba(0,0,0,.25),inset 0 0 0 5px var(--gilt-soft);
  display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:8px}
.sr-frame::before{content:'';position:absolute;inset:6px;border:1px solid rgba(240,217,163,.28);border-radius:7px;pointer-events:none}
.sr-frame.rev .sr-img{transform:rotate(180deg)}
.sr-frame.gated{filter:saturate(.4) brightness(.8)}
.sr-img{width:100%;height:100%;object-fit:cover;border-radius:7px}
.sr-glyph{font-size:calc(var(--cw)*.34);color:#f0d9a3;text-shadow:0 0 18px rgba(240,217,163,.5);line-height:1}
.sr-fname{margin-top:6px;font-family:var(--serif);font-size:calc(var(--cw)*.10);color:#e9dcff;letter-spacing:.03em;line-height:1.2}

/* layout shells */
.sr-stack{display:block}
/* A lone card and its words share a top edge; centred against each other the
   paragraph sat a head lower than the card. */
.sr-stack .sr-spot{align-items:start}
.sr-step.sealed{opacity:.5;cursor:default;border-style:dashed}
.sr-step.sr-step-pulled{border-style:dashed;border-color:#c9a94e;color:#8a6a2a}
.sr-step.sealed::before{content:'✦ ';opacity:.8}
.sr-waiting{font-style:italic;color:var(--ink-soft);text-align:center}
/* The closing card arrives: a slow lift, as the card is turned. */
.sr-spot.sr-closing .sr-scard{animation:sr-turn .9s cubic-bezier(.2,.6,.2,1) both}
@keyframes sr-turn{from{opacity:0;transform:translateY(14px) scale(.96)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){.sr-spot.sr-closing .sr-scard{animation:none}}
.sr-side{display:grid;grid-template-columns:auto 1fr;gap:clamp(18px,3vw,40px);align-items:start}
.sr-side .sr-spot{grid-template-columns:1fr;text-align:center;justify-items:center}
/* line spreads: full-width card|meaning spotlight — the navigator is the word
   strip below the header (no preview-card rail). */
.sr-linebody{display:block}
.sr-linebody .sr-spot{grid-template-columns:auto minmax(0,1fr);text-align:left;justify-items:stretch;align-items:stretch;margin-top:0}
/* text column fills the card height: strip pinned top, paragraph cluster centered */
.sr-linebody .sr-stext{display:flex;flex-direction:column}
.sr-linebody .sr-mid{margin:auto 0}

/* position word-strip (line spreads): clickable labels below the header,
   same navigation as the spotlight's Prev/Next. */
/* film strip: never wraps — centers when it fits, scrolls horizontally when it
   doesn't (safe center keeps the first chip reachable instead of clipped). */
/* position tabs = a CAROUSEL: arrows flank a horizontally-scrolling strip so the
   tabs never force the spotlight column wide (that overflow was shoving the card
   off-center). Arrows cycle the spotlight; the active tab scrolls into view. */
.sr-stripwrap{display:flex;align-items:center;gap:6px;width:100%;max-width:100%;min-width:0;margin:8px auto 22px;box-sizing:border-box}
/* Touch: the positions row stays in reach while the meaning scrolls under it,
   and every step, arrow and Summary is a 44px target. */
@media (pointer:coarse){
  .sr-stripwrap{position:sticky;top:0;z-index:4;padding:6px 0;background:linear-gradient(180deg,#faf5ea 82%,rgba(250,245,234,0))}
  .sr-step{min-height:44px;display:inline-flex;align-items:center}
  .sr-striparrow{width:44px;height:44px}
  .sr-nav button{min-height:44px;padding:10px 18px}
}
/* flex-basis:0 (not auto) so the strip takes the space LEFT by the arrows and
   scrolls its tabs inside it, instead of ballooning to its content width and
   shoving the arrows off-screen. */
.sr-strip{display:flex;flex:1 1 0;min-width:0;flex-wrap:nowrap;justify-content:safe center;gap:10px;
  overflow-x:auto;padding-bottom:4px;-webkit-overflow-scrolling:touch;scrollbar-width:none}
.sr-strip::-webkit-scrollbar{display:none}
/* Five tabs or fewer: they wrap, centred, and no word is ever cut at an edge.
   More than five: the carousel, its edges fading so a tab there reads as more. */
.sr-strip.sr-strip-wrap{flex-wrap:wrap;overflow-x:visible;justify-content:center;row-gap:8px;padding-bottom:0}
/* A phone: the wrapped tabs are compact so they share rows (two at most for
   a gift's three); each still has a 44px touch target through its ::after. */
@media (max-width:520px){
  .sr-strip.sr-strip-wrap{gap:12px 6px}
  .sr-strip.sr-strip-wrap .sr-step{position:relative;min-height:32px;padding:7px 9px;font-size:11px;letter-spacing:.02em}
  .sr-strip.sr-strip-wrap .sr-step::after{content:'';position:absolute;inset:-6px -3px}
}
.sr-strip:not(.sr-strip-wrap){-webkit-mask-image:linear-gradient(90deg,transparent 0,#000 14px,#000 calc(100% - 22px),transparent 100%);
  mask-image:linear-gradient(90deg,transparent 0,#000 14px,#000 calc(100% - 22px),transparent 100%)}
.sr-strip .sr-step{flex:0 0 auto}
.sr-striparrow{flex:0 0 auto;width:30px;height:30px;border-radius:50%;border:1px solid var(--lilac);
  background:#fff;color:var(--veil);font-family:var(--serif);font-size:19px;line-height:1;cursor:pointer;
  display:flex;align-items:center;justify-content:center;transition:.15s;box-shadow:0 2px 8px -3px rgba(78,39,118,.3)}
.sr-striparrow:hover:not(:disabled){border-color:var(--gilt);color:var(--gilt);transform:translateY(-1px)}
.sr-striparrow:disabled{opacity:.28;cursor:default;box-shadow:none}
/* Touch: 44px arrows. Here, after the 30px base rule: in the block above it
   lost by source order (e2e phone walk 2026-10-30, "Next card 30x30"). */
@media (pointer:coarse){.sr-striparrow{width:44px;height:44px}}
/* Let both spotlight columns shrink to the pane so the tab strip's content can't
   stretch the grid — this is what keeps the card centered on narrow screens. */
.sr-spot>*{min-width:0}
.sr-step{font-family:var(--serif);font-size:13px;letter-spacing:.09em;text-transform:uppercase;
  color:var(--ink-soft);background:var(--lilac-soft);border:1px solid var(--lilac);border-radius:999px;
  padding:9px 20px;cursor:pointer;transition:.15s;line-height:1.1}
.sr-step:hover{border-color:var(--gilt)}
.sr-step.sel{color:var(--veil);background:#fff;border-color:var(--gilt);font-weight:700;
  box-shadow:0 3px 12px -3px rgba(78,39,118,.35)}

/* spotlight */
.sr-spot{display:grid;grid-template-columns:auto minmax(0,1fr);gap:clamp(22px,4vw,42px);align-items:center;margin-top:14px}
.sr-scard{display:flex;flex-direction:column;align-items:center;gap:10px}
.sr-big{--cw:150px;--ch:238px}
/* The words that name a card are its link: the same gilt underline whether
   they sit in the prose (a button that inherits the paragraph's type) or under
   the spotlight card. */
.sr-cardlink{cursor:pointer;color:var(--veil);text-decoration:underline;text-decoration-color:var(--gilt-soft);text-underline-offset:3px;text-decoration-thickness:1px}
.sr-cardlink:hover,.sr-cardlink:focus-visible{text-decoration-color:var(--gilt);outline:none}
button.sr-cardlink{font:inherit;color:inherit;background:none;border:0;padding:0;margin:0;display:inline;line-height:inherit}
/* The card's name under the card: a 44px hit area round its one line. */
div.sr-cardlink{position:relative}
div.sr-cardlink::after{content:'';position:absolute;inset:-14px -8px}
.sr-nav{display:flex;gap:8px;justify-content:center}
/* Prev/Next centered below the meaning paragraph */
.sr-nav-under{justify-content:center;margin-top:22px}
/* The reveal at the foot of the weave: the one step left in the reading, so it
   carries the gilt and a little more presence than the paging buttons. */
.sr-foot-nav{margin:22px 0 6px;flex-wrap:wrap;justify-content:flex-start}
.sr-foot-body > .sr-foot-nav{margin:30px auto 6px;justify-content:center}
.sr-foot-nav .sr-reveal{color:var(--ink);border-color:var(--gilt);background:linear-gradient(180deg,#fff,#f7ecd8);font-size:13px;padding:8px 20px;box-shadow:0 2px 10px rgba(184,136,74,.25)}
.sr-foot-nav .sr-reveal:hover{box-shadow:0 3px 14px rgba(184,136,74,.4)}
.sr-nav button{font-family:var(--serif);font-size:12px;color:var(--veil);background:#fff;border:1px solid var(--lilac);border-radius:999px;padding:6px 14px;cursor:pointer}
.sr-nav button:hover{border-color:var(--gilt)}
.sr-mcard{font-family:var(--serif);color:var(--veil);font-weight:600;font-size:18px;margin:8px 0}
/* card name when shown beneath the card (left column) */
.sr-mcard-under{text-align:center;margin:8px 0 2px;font-size:16px;max-width:220px;line-height:1.25}
.sr-meaning{font-size:20px;line-height:1.68;color:var(--ink);white-space:pre-line;text-align:justify;
  hyphens:auto;background:rgba(255,255,255,.55);border:1px solid var(--lilac);border-radius:14px;
  padding:20px 26px;box-shadow:0 6px 20px -8px rgba(78,39,118,.18);max-width:640px;margin:0 auto}
.sr-locked{font-size:18px;color:var(--ink-soft);font-style:italic}

/* tiny-card map (celtic) */
.sr-mapbox{background:rgba(255,255,255,.5);border:1px solid var(--gilt-soft);border-radius:14px;padding:16px 18px;position:sticky;top:12px}
.sr-map-title{font-family:var(--serif);font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--ink-soft);text-align:center;margin-bottom:14px}
.sr-tmap{display:flex;gap:16px;align-items:center;justify-content:center}
.sr-tcross{display:grid;grid-template-columns:repeat(3,var(--tw));grid-template-rows:repeat(3,var(--th));gap:9px 11px;align-items:center;justify-items:center}
.sr-tcross .p5{grid-area:1/2}.sr-tcross .p4{grid-area:2/1}.sr-pc{grid-area:2/2;position:relative;width:var(--tw);height:var(--th)}
.sr-tcross .p6{grid-area:2/3}.sr-tcross .p3{grid-area:3/2}
.sr-pc-a{position:absolute;inset:0}.sr-pc-b{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(90deg);z-index:2}
.sr-tstaff{display:grid;grid-template-rows:repeat(4,var(--th));gap:9px}
.sr-tc{position:relative;width:var(--tw);height:var(--th);border-radius:6px;overflow:hidden;cursor:pointer;transition:transform .15s;
  background:linear-gradient(160deg,#3a2566,#1c1030 60%,#2a1845);border:1.5px solid var(--gilt-soft);
  box-shadow:0 3px 8px rgba(0,0,0,.35),inset 0 0 0 2px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;padding:0}
.sr-tc:hover{transform:scale(1.06)}
.sr-tc .sr-tg{font-size:calc(var(--tw)*.5);color:#f0d9a3;text-shadow:0 0 10px rgba(240,217,163,.5)}
.sr-tc::before{content:'';position:absolute;inset:3px;border:1px solid rgba(240,217,163,.25);border-radius:4px}
.sr-num{position:absolute;top:-6px;left:-6px;width:16px;height:16px;border-radius:50%;background:var(--gilt);color:#1c1030;
  font-family:var(--serif);font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 3px rgba(0,0,0,.4)}
.sr-tc.sel{border-color:#f0d9a3;box-shadow:0 0 0 3px rgba(240,217,163,.6),0 4px 12px rgba(0,0,0,.5)}
.sr-tc.sel .sr-num{background:#f0d9a3}
.sr-tc.gated{filter:saturate(.4) brightness(.8)}

/* footer — centered title, then the weave + a large illustration, below the cards */
.sr-foot{margin-top:32px;border-top:1px dashed var(--gilt-soft);padding-top:26px}
.sr-foot-title{font-family:var(--serif);color:var(--ink);font-weight:600;font-size:22px;letter-spacing:.02em;text-align:center;margin:0 0 20px}
/* Top-aligned: the weave and the picture are two readings of the same thing
   and should start on the same line. Centering made the text float. */
.sr-foot-body{position:relative;display:grid;grid-template-columns:1fr minmax(300px,420px);gap:38px;align-items:start}
/* 1-card draw (no synthesis/summary): center the lone illustration instead of
   leaving it pinned to the empty left column. */
.sr-foot-body.sr-foot-solo{grid-template-columns:minmax(0,480px);justify-content:center}
/* Sealed: collapsed to nothing, but still in the document so the illustration
   downloads while the seeker reads the cards. Not display:none — that would
   cancel the preload the old scrim existed to protect. */
.sr-foot-sealed{height:0;margin:0;padding:0;border:none;overflow:hidden;visibility:hidden;pointer-events:none}
/* Closing mark — the reading ends, rather than just stopping. */
.sr-end{display:flex;align-items:center;gap:18px;margin:38px auto 4px;max-width:340px;
  color:var(--gilt);font-size:13px;line-height:1}
.sr-end::before,.sr-end::after{content:'';flex:1;height:1px;
  background:linear-gradient(90deg,transparent,var(--gilt-soft))}
.sr-end::after{background:linear-gradient(90deg,var(--gilt-soft),transparent)}
.sr-ill{margin:0}
/* The picture's line (picture-caption DESIGN.md, 2026-10-30): Letter Serif
   italic in the soft ink, sentence case, in two beats; it rises in once the
   picture has loaded. */
.sr-ill-cap{font:italic 500 17px/1.4 'Letter Serif','Cormorant Garamond',Georgia,serif;color:#5a4a72;text-align:center;text-wrap:balance;max-width:34ch;margin:12px auto 0;
  animation:sr-cap-in 520ms cubic-bezier(.2,.7,.2,1) 280ms both}
@keyframes sr-cap-in{from{opacity:0;transform:translateY(6px)}}
@media (prefers-reduced-motion: reduce){.sr-ill-cap{animation:none}}
/* The summary fits its box (utils/summary-fit): `.sr-syn` is the size
   container; the length sets --syn-n (17 to 24), the box's width eases it
   down on a narrow screen, never under 17px. Long words wrap, never overflow. */
.sr-syn{container-type:inline-size;min-width:0}
.sr-syn p{font-size:clamp(17px, calc(var(--syn-n, 17) * 0.7px + var(--syn-n, 17) * 0.0556cqi), calc(var(--syn-n, 17) * 1px));line-height:1.55;color:var(--ink);margin:0;border-left:2px solid var(--gilt);padding-left:14px;white-space:pre-line;overflow-wrap:anywhere}
.sr-ill{display:flex;flex-direction:column;align-items:center}
.sr-ill .sr-slot{width:100%;border:1.5px dashed var(--lilac);border-radius:14px;min-height:300px;display:flex;align-items:center;justify-content:center;background:linear-gradient(160deg,#fff,rgba(212,190,240,.10));margin-top:8px;overflow:hidden}
.sr-ill-img{width:100%;height:auto;display:block}
.sr-zoomable{cursor:zoom-in}

@container (max-width:680px){
  .sr-ill-cap{font-size:16.5px}
  .sr-side{grid-template-columns:1fr;justify-items:center}
  .sr-side .sr-mapbox{position:static}
  .sr-spot,.sr-linebody .sr-spot{grid-template-columns:minmax(0,1fr);text-align:center;justify-items:center}
  /* The card is content-sized + centered (justify-items:center), but the text
     column must STRETCH to the pane — otherwise justify-items:center sizes it to
     the tab strip's natural width (blowing past the pane and shoving the arrows
     off-screen). Stretch it and cap it to the container. */
  .sr-spot>.sr-stext,.sr-linebody .sr-stext{justify-self:stretch;width:100%;max-width:100%;min-width:0}
  .sr-foot-body{grid-template-columns:1fr}
}

/* ── Narrow screens: the whole card step has to fit above the fold ──────────
   At 379×800 the reading was taller than the pane and cut off mid-sentence:
   a 238px card, a 20px justified paragraph and generous framing left nothing
   for the meaning. Everything below shrinks the frame, not the words — the
   meaning keeps a readable size while the card, which the seeker has already
   looked at, gives up the room. */
/* The root's own padding can't be set from its own container query — an
   element is a container for its descendants, not for itself. So the frame is
   tightened from the two places that actually describe a narrow render: a real
   narrow viewport (the live reading) and the Studio's mobile preview class. */
@media (max-width:430px){
  .spread-reading{padding:14px 13px 16px;border-radius:12px}
}
.sr-vp-mobile{padding:14px 13px 16px;border-radius:12px}

@container (max-width:430px){
  /* Corner clearance is NOT set here — the fixed account portrait belongs to
     the live page, and reserving space for it in the shared component would
     put a hole at the top of the Studio preview, which has no portrait. The
     live host adds it (SpreadReadingLive, .srl-response). */
  .sr-hero{margin-bottom:2px}
  .sr-hero h1{font-size:17px;line-height:1.25}
  .sr-rule{margin-top:8px;width:84px}

  .sr-spot{margin-top:8px;gap:10px}
  .sr-big{--cw:96px;--ch:152px}
  .sr-scard{gap:4px}
  .sr-mcard-under{font-size:13px;margin:4px 0 0;max-width:100%}

  .sr-stripwrap{margin-top:2px}
  .sr-step{font-size:11px;padding:6px 13px}
  .sr-mid{margin-top:2px}
  /* Left-aligned reads better than justified at this width — justification
     opens rivers between the words in a ~34-character line. */
  .sr-meaning{font-size:15px;line-height:1.5;padding:11px 13px;border-radius:11px;
    text-align:left;hyphens:none}
  .sr-nav-under{margin-top:10px}
  .sr-nav button{font-size:12px;padding:7px 15px}

  /* The summary is the one thing deliberately below the fold — it is scrolled
     to, so it can be as tall as it needs. */
  .sr-foot{margin-top:18px;padding-top:16px}
  .sr-foot-title{font-size:18px;margin-bottom:12px}
  .sr-ill .sr-slot{min-height:180px}
}
</style>
